/**
 * useOosValidation — out-of-sample validation of the published edge.
 *
 * Fetches /api/oos-validation once per session (module-level cache). The payload
 * is the honest counter to in-sample selection bias (whitepaper §6 / §7.2):
 *   - per-window expanding walk-forward, where the forecast horizon is
 *     re-committed on prior folds before each fold is scored out-of-sample; and
 *   - a single White's Reality Check across the whole (window × horizon ×
 *     conviction-bin) selection grid — the p-value that the best-looking rule's
 *     edge survives the search. See OutOfSampleValidation.
 */
import { useEffect, useState } from "react";
import requests from "../requests";

export interface OosWindow {
  name: string;                          // "2w" | "4w" | "3m" | "6m" | "1y"
  committed_horizon: number;
  committed_horizons_by_fold: number[];
  oos_win_rate: number | null;
  oos_win_rate_ci: [number, number] | null;
  oos_avg_return: number | null;
  oos_avg_return_ci: [number, number] | null;
  blended_base_rate: number | null;
  oos_edge_over_base: number | null;
  n_signals: number;
  n_dates: number;
}

export interface GridRealityCheck {
  n_rules: number;
  best_rule: { window: number; horizon: number; conviction_bin: [number, number] } | null;
  best_rule_edge_over_base_pct: number | null;
  reality_check_p: number | null;
  bonferroni_p: number | null;
  note?: string;
}

export interface OosValidationData {
  windows: Record<string, OosWindow>;
  grid_reality_check: GridRealityCheck | null;
  methodology: string;
  params?: { n_folds: number; min_train: number; bootstrap_B: number; rng_seed: number };
}

// Module-level cache shared across components.
let _cache: OosValidationData | null = null;
let _inflight: Promise<void> | null = null;

const EMPTY: OosValidationData = { windows: {}, grid_reality_check: null, methodology: "" };

export function useOosValidation(): OosValidationData | null {
  const [data, setData] = useState<OosValidationData | null>(_cache);

  useEffect(() => {
    if (_cache) { setData(_cache); return; }

    if (!_inflight) {
      _inflight = requests.get("/api/oos-validation")
        // Merge over EMPTY so a partial/malformed payload (e.g. an unpopulated pkl that
        // serializes to {} or null, or a proxy error body) can never leave `windows`
        // undefined — the component indexes windows[k] directly.
        .then(r => { _cache = { ...EMPTY, ...((r.data as Partial<OosValidationData> | null) ?? {}) }; })
        .catch(() => { _cache = EMPTY; })
        .finally(() => { setData(_cache); });
    } else {
      void _inflight.finally(() => { setData(_cache); });
    }
  }, []);

  return data;
}
